import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Copy, Check, ChevronsUpDown, Pencil } from "lucide-react";
import { cn, formatDateSafe } from "@/lib/utils";
import { Client, Employee, PlanRate } from "@shared/schema";
import { useState } from "react";
import {
  Building,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import React from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { getSicCodeLabel, SIC_CODES, formatSicCode, resolveSicCodeId } from "@shared/sicCodes";
import { clientSinceInputValue } from "@/lib/clientSince";
import { ENTITY_TYPES } from "@shared/entityTypes";
import AgencyRepresentatives from "@/components/AgencyRepresentatives";
import { EmployeeDefaultSettingsCard } from "@/components/EmployeeDefaultSettingsCard";
import { formatEINForDisplay, formatEINInputValue } from "@/lib/ein";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import ContactHistoryTab from "./ContactHistoryTab";
import type { ContactInteraction } from "@shared/schema";

export default function ClientProfile({
  client,
  employees,
  contactInteractions = [],
  contactInteractionsLoading = false,
}: {
  client: Client;
  employees: Employee[];
  contactInteractions?: ContactInteraction[];
  contactInteractionsLoading?: boolean;
}) {
  const primaryEmployee = employees.find(
    (e) => e.id === (client as any).primaryContactEmployeeId
  );
  // What the broker entered on the client wins; the linked employee only fills
  // gaps (and provides the profile link when it is the same person).
  const employeeName = primaryEmployee
    ? `${primaryEmployee.firstName} ${primaryEmployee.lastName}`.trim()
    : "";
  const primaryName = (client.primaryContactName || "").trim() || employeeName;
  const primaryEmail = client.contactEmail || primaryEmployee?.email || "";
  const primaryPhone = client.contactPhone || primaryEmployee?.phone || "";
  const linkedEmployee =
    primaryEmployee && employeeName.toLowerCase() === primaryName.toLowerCase()
      ? primaryEmployee
      : null;
  const formatPhone = (raw?: string | null) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
        7
      )}`;
    }
    return String(raw).trim();
  };
  // --- Login Credentials management ---
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // --- Inline section editing ---
  // Each profile card can flip into edit mode and PATCH just its own fields,
  // as an alternative to the full Edit Client dialog.
  const [editingContact, setEditingContact] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sicOpen, setSicOpen] = useState(false);
  const [contactDraft, setContactDraft] = useState<Record<string, string>>({});
  const [companyDraft, setCompanyDraft] = useState<Record<string, any>>({});

  const clientEmployees = employees.filter((e) => e.clientId === client.id);

  const startContactEdit = () => {
    setContactDraft({
      primaryContactName: client.primaryContactName || "none",
      contactEmail: client.contactEmail || "",
      contactPhone: client.contactPhone || "",
      website: client.website || "",
      address1: (client as any).address1 || "",
      address2: (client as any).address2 || "",
      city: (client as any).city || "",
      state: (client as any).state || "",
      zipCode: (client as any).zipCode || "",
    });
    setEditingContact(true);
  };

  const startCompanyEdit = () => {
    setCompanyDraft({
      status: client.status || "active",
      entityType: client.entityType || "",
      ein: client.ein || "",
      sicCodeId: resolveSicCodeId((client as any).sicCodeId, client.sic),
      clientSince: clientSinceInputValue(client as any),
    });
    setEditingCompany(true);
  };

  const saveSection = async (payload: Record<string, any>, close: () => void) => {
    setSectionSaving(true);
    try {
      await apiRequest("PATCH", `/api/clients/${client.id}`, payload);
      queryClient.invalidateQueries({ queryKey: ["/api/clients", String(client.id)], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"], refetchType: "all" });
      close();
      toast({ title: "Client updated successfully", variant: "success" });
    } catch (err: any) {
      toast({
        title: "Failed to update client",
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSectionSaving(false);
    }
  };

  const saveContactSection = () => {
    const addressLine = [
      contactDraft.address1,
      contactDraft.address2,
      [contactDraft.city, contactDraft.state].filter(Boolean).join(", "),
      contactDraft.zipCode,
    ]
      .filter((s) => s && s.trim().length > 0)
      .join(", ");
    saveSection(
      {
        primaryContactName:
          contactDraft.primaryContactName === "none"
            ? null
            : contactDraft.primaryContactName,
        contactEmail: contactDraft.contactEmail,
        contactPhone: contactDraft.contactPhone,
        website: contactDraft.website,
        address1: contactDraft.address1,
        address2: contactDraft.address2,
        city: contactDraft.city,
        state: contactDraft.state,
        zipCode: contactDraft.zipCode,
        address: addressLine,
      },
      () => setEditingContact(false)
    );
  };

  const saveCompanySection = () => {
    saveSection(
      {
        status: companyDraft.status,
        entityType: companyDraft.entityType,
        ein: companyDraft.ein,
        sicCodeId: companyDraft.sicCodeId,
        clientSince: companyDraft.clientSince
          ? new Date(companyDraft.clientSince + "T00:00:00")
          : null,
      },
      () => setEditingCompany(false)
    );
  };
  const [credsLoading, setCredsLoading] = useState(false);
  const [isResetLinkConfirmOpen, setIsResetLinkConfirmOpen] = useState(false);
  const [tempPasswordDialogOpen, setTempPasswordDialogOpen] = useState(false);
  const [tempPasswordEmailUser, setTempPasswordEmailUser] = useState(true);
  const [tempPasswordResult, setTempPasswordResult] = useState<{
    tempPassword?: string;
    emailSent: boolean;
    email: string;
  } | null>(null);
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false);

  const sendLogin = async (
    mode: "reset_link" | "temp_password",
    sendEmail: boolean,
  ) => {
    const res = await apiRequest("POST", `/api/clients/${client.id}/send-login`, {
      mode,
      sendEmail,
    });
    return res.json();
  };

  const handleSendResetLink = async () => {
    setCredsLoading(true);
    try {
      const data = await sendLogin("reset_link", true);
      toast({
        title: data.isNewUser ? "Setup link sent" : "Reset link sent",
        description: `Emailed to ${data.email}. Link expires in 7 days.`,
        variant: "success",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || "Failed to send reset link",
        variant: "destructive",
      });
    } finally {
      setCredsLoading(false);
    }
  };

  const handleGenerateTempPassword = async () => {
    setCredsLoading(true);
    setTempPasswordCopied(false);
    try {
      const data = await sendLogin("temp_password", tempPasswordEmailUser);
      setTempPasswordResult({
        tempPassword: data.tempPassword,
        emailSent: !!data.emailSent,
        email: data.email,
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || "Failed to generate temporary password",
        variant: "destructive",
      });
      setTempPasswordDialogOpen(false);
    } finally {
      setCredsLoading(false);
    }
  };

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                Contact Information
              </span>
              {!editingContact && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-muted-foreground"
                  onClick={startContactEdit}
                  title="Edit contact information"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          {editingContact ? (
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Primary Contact</label>
                <Select
                  value={contactDraft.primaryContactName}
                  onValueChange={(v) => setContactDraft((d) => ({ ...d, primaryContactName: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select primary contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No primary contact</SelectItem>
                    {clientEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={`${emp.firstName} ${emp.lastName}`}>
                        {emp.firstName} {emp.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Contact Email</label>
                  <Input
                    type="email"
                    className="h-9"
                    value={contactDraft.contactEmail}
                    onChange={(e) => setContactDraft((d) => ({ ...d, contactEmail: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Contact Phone</label>
                  <Input
                    className="h-9"
                    value={contactDraft.contactPhone}
                    onChange={(e) => setContactDraft((d) => ({ ...d, contactPhone: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Website</label>
                <Input
                  className="h-9"
                  placeholder="https://example.com"
                  value={contactDraft.website}
                  onChange={(e) => setContactDraft((d) => ({ ...d, website: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Address 1</label>
                  <Input
                    className="h-9"
                    value={contactDraft.address1}
                    onChange={(e) => setContactDraft((d) => ({ ...d, address1: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Address 2</label>
                  <Input
                    className="h-9"
                    placeholder="Apt, Suite, etc."
                    value={contactDraft.address2}
                    onChange={(e) => setContactDraft((d) => ({ ...d, address2: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">City</label>
                  <Input
                    className="h-9"
                    value={contactDraft.city}
                    onChange={(e) => setContactDraft((d) => ({ ...d, city: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">State</label>
                  <Input
                    className="h-9"
                    value={contactDraft.state}
                    onChange={(e) => setContactDraft((d) => ({ ...d, state: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Zip</label>
                  <Input
                    className="h-9"
                    value={contactDraft.zipCode}
                    onChange={(e) => setContactDraft((d) => ({ ...d, zipCode: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingContact(false)}
                  disabled={sectionSaving}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={saveContactSection} disabled={sectionSaving}>
                  {sectionSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            </CardContent>
          ) : (
          <CardContent className="space-y-4">
            {primaryName && (
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-gray-400" />
                {linkedEmployee ? (
                  <Link
                    href={`/clients/${client.id}/employees/${linkedEmployee.id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {primaryName}
                  </Link>
                ) : (
                  <span>{primaryName}</span>
                )}
              </div>
            )}
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-gray-400" />
              {primaryEmail ? (
                <a
                  href={`mailto:${primaryEmail}`}
                  className="text-blue-600 hover:underline"
                >
                  {primaryEmail}
                </a>
              ) : (
                <span></span>
              )}
            </div>
            {primaryPhone && (
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{formatPhone(primaryPhone)}</span>
              </div>
            )}
            {client.website && (
              <div className="flex items-center space-x-3">
                <Building className="h-4 w-4 text-gray-400" />
                <a
                  href={client.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {client.website}
                </a>
              </div>
            )}
            {client.address && (
              <div className="flex items-center space-x-3">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span>{client.address}</span>
              </div>
            )}

            {/* Login Credentials Section */}
            {primaryEmail && (
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                    <KeyRound className="h-3.5 w-3.5" />
                    Employer Portal Login
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        disabled={credsLoading}
                      >
                        {credsLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Mail className="h-3 w-3" />
                        )}
                        Send login
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setIsResetLinkConfirmOpen(true)}>
                        Send password reset link
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setTempPasswordResult(null);
                          setTempPasswordEmailUser(true);
                          setTempPasswordDialogOpen(true);
                        }}
                      >
                        Generate temporary password…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Reset link is the default. Generate a temp password only if the user can't use email links.
                </p>
              </div>
            )}
          </CardContent>
          )}
        </Card>

        {/* Company Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Building className="h-5 w-5 mr-2" />
                Company Details
              </span>
              {!editingCompany && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-muted-foreground"
                  onClick={startCompanyEdit}
                  title="Edit company details"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          {editingCompany ? (
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Status</label>
                  <Select
                    value={companyDraft.status}
                    onValueChange={(v) => setCompanyDraft((d) => ({ ...d, status: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Entity Type</label>
                  <Select
                    value={companyDraft.entityType}
                    onValueChange={(v) => setCompanyDraft((d) => ({ ...d, entityType: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map((et) => (
                        <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">EIN</label>
                <Input
                  className="h-9"
                  placeholder="XX-XXXXXXX"
                  value={companyDraft.ein || ""}
                  onChange={(e) =>
                    setCompanyDraft((d) => ({ ...d, ein: formatEINInputValue(e.target.value) }))
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">SIC Code</label>
                <Popover open={sicOpen} onOpenChange={setSicOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={sicOpen}
                      className="w-full h-9 flex items-center justify-between overflow-hidden font-normal"
                    >
                      <span className="truncate flex-1 text-left">
                        {companyDraft.sicCodeId
                          ? getSicCodeLabel(companyDraft.sicCodeId)
                          : "Select SIC code"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    <Command>
                      <CommandInput placeholder="Search code or industry" />
                      <CommandEmpty>No results found.</CommandEmpty>
                      <CommandList style={{ maxHeight: "250px", overflowY: "auto" }}>
                        <CommandGroup>
                          {SIC_CODES.map((opt) => (
                            <CommandItem
                              key={opt.code}
                              value={`${formatSicCode(opt.code)} ${opt.industryTitle}`}
                              onSelect={() => {
                                setCompanyDraft((d) => ({ ...d, sicCodeId: opt.code }));
                                setSicOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  opt.code === companyDraft.sicCodeId ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {formatSicCode(opt.code)} - {opt.industryTitle}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Client Since</label>
                <Input
                  type="date"
                  className="h-9"
                  value={companyDraft.clientSince || ""}
                  onChange={(e) => setCompanyDraft((d) => ({ ...d, clientSince: e.target.value }))}
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingCompany(false)}
                  disabled={sectionSaving}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={saveCompanySection} disabled={sectionSaving}>
                  {sectionSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            </CardContent>
          ) : (
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-500">Status:</span>
              <Badge
                variant={client.status === "active" ? "default" : "secondary"}
              >
                {client.status}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Active Employees:</span>
              <span className="font-medium">
                {
                  employees.filter(
                    (emp) =>
                      emp.clientId === client.id &&
                      String(emp.status).toLowerCase() === "active"
                  ).length
                }
              </span>
            </div>
            {client.entityType && (
              <div className="flex justify-between">
                <span className="text-gray-500">Entity Type:</span>
                <span className="font-medium">{client.entityType}</span>
              </div>
            )}
            {client.ein && (
              <div className="flex justify-between">
                <span className="text-gray-500">EIN:</span>
                <span className="font-medium">{formatEINForDisplay(client.ein)}</span>
              </div>
            )}
            {((client as any).sicCodeId || client.sic) && (
              <div className="flex justify-between">
                <span className="text-gray-500">SIC:</span>
                <span className="font-medium">
                  {(client as any).sicCodeId
                    ? getSicCodeLabel((client as any).sicCodeId)
                    : client.sic}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Client Since:</span>
              <span className="font-medium">
                {formatDateSafe((client as any).clientSince || client.createdAt)}
              </span>
            </div>
          </CardContent>
          )}
        </Card>
      </div>

      {/* Agency Representatives & Defaults */}
      <div className="grid gap-6 md:grid-cols-2">
        <AgencyRepresentatives
          entityType="client"
          entityId={client.id}
          currentTeamId={client.teamId}
        />
        <EmployeeDefaultSettingsCard
          ownerType="client"
          ownerId={String(client.id)}
          heading="Employee Defaults"
          description="Control which values are prefilled when this client's employees are created."
        />
      </div>

      {/* Inline contact history — relocated from the (removed) Contact History
          top-level tab. Renders the full table with its own Add Contact dialog. */}
      <ContactHistoryTab
        contactInteractions={contactInteractions}
        contactInteractionsLoading={contactInteractionsLoading}
        clientId={String(client.id)}
        clientName={client.name}
      />

      <AlertDialog open={isResetLinkConfirmOpen} onOpenChange={setIsResetLinkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send password reset link?</AlertDialogTitle>
            <AlertDialogDescription>
              An email with a password setup link will be sent to{" "}
              <strong>{primaryEmail}</strong>. Any existing setup link for this user will be invalidated. The link expires in 7 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsResetLinkConfirmOpen(false);
                handleSendResetLink();
              }}
            >
              Send link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={tempPasswordDialogOpen}
        onOpenChange={(open) => {
          setTempPasswordDialogOpen(open);
          if (!open) setTempPasswordResult(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate temporary password</DialogTitle>
            <DialogDescription>
              For {primaryEmail}. The user will be required to change it on first login.
            </DialogDescription>
          </DialogHeader>

          {tempPasswordResult ? (
            <div className="space-y-3 py-2">
              <div className="rounded-md border bg-gray-50 dark:bg-gray-800 p-3 font-mono text-sm break-all">
                {tempPasswordResult.tempPassword}
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {tempPasswordResult.emailSent
                    ? `Emailed to ${tempPasswordResult.email}.`
                    : "Not emailed — copy and share manually."}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!tempPasswordResult.tempPassword) return;
                    try {
                      await navigator.clipboard.writeText(tempPasswordResult.tempPassword);
                      setTempPasswordCopied(true);
                    } catch {
                      // ignore
                    }
                  }}
                  className="gap-1"
                >
                  {tempPasswordCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {tempPasswordCopied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={tempPasswordEmailUser}
                  onCheckedChange={(v) => setTempPasswordEmailUser(!!v)}
                />
                Also email the temporary password to {primaryEmail}
              </label>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Note: any existing password and pending setup link for this user will be invalidated.
              </p>
            </div>
          )}

          <DialogFooter>
            {tempPasswordResult ? (
              <Button onClick={() => setTempPasswordDialogOpen(false)}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setTempPasswordDialogOpen(false)} disabled={credsLoading}>
                  Cancel
                </Button>
                <Button onClick={handleGenerateTempPassword} disabled={credsLoading}>
                  {credsLoading ? "Generating…" : "Generate"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
